import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Calculator } from 'lucide-react';
import { Modal, Button, Input, Select } from './UI';
import { ReviewCheckbox } from './ReviewMark';
import { PaymentDrafts, TBANK_BIK, useTbankOrgs } from './bank/PaymentDrafts';
import { useData } from '../App';
import { ACCRUAL_LABELS, AccrualType, CompanyProfile, Counterparty, PaymentMethod, PayrollPreview, Project, SalaryPart, Transaction,
         TransactionStatus, TransactionType, VacationEstimate } from '../types';
import { CrmDebtHint, OrgSelect, PayKindTabs, ProjectSelect, useCrmBalances, useOrgChoice } from './FormParts';
import { api } from '../services/api';
import { accountLabel, defaultSalaryPart, defaultSalaryPeriod, formatDay, formatMoney, periodLabel, todayISO } from '../utils/format';

export type PayType = 'SALARY' | 'UNOFFICIAL_SALARY';

const ACCRUAL_TITLES: Record<AccrualType, string> = {
    SALARY: 'Зарплата', BONUS: 'Премия', VACATION: 'Отпускные', SICK: 'Больничный', UNUSED_VACATION: 'Компенсация отпуска', MATERIAL_AID: 'Материальная помощь',
};
const addDays = (iso: string, n: number) => { const d = new Date(iso + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };

// Выплата сотрудникам: один или несколько, одна кнопка «Провести».
// Вид выплаты — вверху: штат, неофициально (по умолчанию наличными) или самозанятый (onSelfEmployed — своя форма).
// Официальная выплата — начисление определённого вида (зарплата, премия, отпускные, больничный…) в любую дату;
// сумма «на руки» (начислено досчитывается по прогрессивной шкале) или «начислено» (расчёт бухгалтера).
// editing — правка одной уже проведённой выплаты
export const PayrollForm: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    orgs: CompanyProfile[];
    fixedOrgId: string | null;
    staff: Counterparty[];
    projects: Project[];
    editing?: Transaction | null;
    initialType?: PayType;
    initialAccrual?: AccrualType;
    initialStaffId?: string;
    onSelfEmployed?: () => void;
    onPosted?: (txs: Transaction[]) => void;
}> = ({ isOpen, onClose, orgs, fixedOrgId, staff, projects, editing, initialType = 'SALARY', initialAccrual = 'SALARY', initialStaffId,
        onSelfEmployed, onPosted }) => {
    const { addTransactions, updateTransaction, showNotification } = useData();
    const [orgId, setOrgId, rememberOrg] = useOrgChoice(isOpen, orgs, fixedOrgId, editing);
    const [reviewed, setReviewed] = useState(false);
    useEffect(() => { setReviewed(false); }, [editing, isOpen]);
    const crmBalances = useCrmBalances(isOpen && !editing);
    const org = orgs.find(o => o.id === orgId);
    const [projectId, setProjectId] = useState('');
    const [date, setDate] = useState(todayISO());
    const [payType, setPayType] = useState<PayType>('SALARY');
    const [accrual, setAccrual] = useState<AccrualType>('SALARY');
    const [grossInput, setGrossInput] = useState(false);
    const [part, setPart] = useState<SalaryPart>('ADVANCE');
    const [period, setPeriod] = useState('');
    const [method, setMethod] = useState<PaymentMethod>('bank');
    const [accountId, setAccountId] = useState('');
    const [amounts, setAmounts] = useState<Record<string, string>>({});
    const [preview, setPreview] = useState<PayrollPreview | null>(null);
    // Отпуск / больничный: период и дни
    const [periodFrom, setPeriodFrom] = useState('');
    const [days, setDays] = useState('');
    const [estimates, setEstimates] = useState<Record<string, VacationEstimate>>({});
    // Неполный месяц: зарплата пропорционально отработанным дням
    const [worked, setWorked] = useState('');
    const [norm, setNorm] = useState('');
    const [withOrder, setWithOrder] = useState(true);

    useEffect(() => {
        if (!isOpen) return;
        const d = editing?.date || todayISO();
        const p = (editing?.salaryPart as SalaryPart) || defaultSalaryPart(d, org);
        setDate(d);
        const type: PayType = editing ? (editing.kind === 'UNOFFICIAL_SALARY' ? 'UNOFFICIAL_SALARY' : 'SALARY') : initialType;
        const acc: AccrualType = editing ? (editing.accrualType || (editing.payoutLabel === 'BONUS' && !editing.salaryPart ? 'BONUS' : 'SALARY')) : initialAccrual;
        setPayType(type);
        setAccrual(acc);
        setGrossInput(editing ? !!editing.grossAmount : acc === 'SICK');
        setPart(p);
        setPeriod(editing?.salaryPeriod ? editing.salaryPeriod.slice(0, 7) : acc === 'SALARY' ? defaultSalaryPeriod(d, p) : d.slice(0, 7));
        setMethod(editing?.paymentMethod || (type === 'UNOFFICIAL_SALARY' ? 'cash' : 'bank'));
        setAccountId(editing?.bankAccountId || org?.activeAccountId || org?.accounts[0]?.id || '');
        setAmounts(editing && editing.counterpartyId ? { [editing.counterpartyId]: String(editing.grossAmount || editing.amount) }
                   : initialStaffId ? { [initialStaffId]: '' } : {});
        setProjectId(editing?.projectId || '');
        setPeriodFrom(editing?.periodFrom || ''); setDays(editing?.days ? String(editing.days) : '');
        setEstimates({}); setWorked(''); setNorm(''); setWithOrder(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, editing, org]);

    const official = payType === 'SALARY';
    // Черновики в Т-Банке (ИП подключён по API): после «Провести» — окно черновиков за эту дату (зарплата, ЕНП, травматизм)
    const tbank = useTbankOrgs();
    const [toBank, setToBank] = useState(true);
    const [drafts, setDrafts] = useState<{ orgId: string; date: string } | null>(null);
    const tbankAccount = org?.accounts.find(a => a.id === accountId)?.bik === TBANK_BIK;
    const canDraft = !editing && official && tbank.includes(orgId) && method === 'bank' && tbankAccount;
    const regular = official && accrual === 'SALARY';

    // Неофициальную обычно платят наличными, официальную — со счёта
    const changeType = (t: PayType) => {
        setPayType(t);
        // Неофициально — только зарплата или премия
        if (t === 'UNOFFICIAL_SALARY' && accrual !== 'BONUS') changeAccrual('SALARY');
        if (!editing) { setMethod(t === 'UNOFFICIAL_SALARY' ? 'cash' : 'bank'); setAmounts(initialStaffId ? { [initialStaffId]: '' } : {}); }
    };
    const changeAccrual = (a: AccrualType) => {
        setAccrual(a);
        setGrossInput(a === 'SICK');
        setPeriod(a === 'SALARY' ? defaultSalaryPeriod(date, part) : date.slice(0, 7));
        setEstimates({});
    };

    const changeDate = (d: string) => {
        setDate(d);
        if (!editing && d) {
            if (accrual === 'SALARY') { const p = defaultSalaryPart(d, org); setPart(p); setPeriod(defaultSalaryPeriod(d, p)); }
            else setPeriod(d.slice(0, 7));
        }
    };

    // Официальную ЗП платит только организация, где сотрудник оформлен; неофициальную — любая
    const people = editing ? staff.filter(s => s.id === editing.counterpartyId)
        : official ? staff.filter(s => s.employedByOrgId === orgId) : staff;
    const notEmployedHere = official && !editing ? staff.filter(s => s.employedByOrgId !== orgId) : [];
    const items = useMemo(() => Object.entries(amounts)
        .map(([counterpartyId, v]) => ({ counterpartyId, amount: Number(v) }))
        .filter(i => i.amount > 0), [amounts]);
    const total = items.reduce((s, i) => s + i.amount, 0);
    const useGross = official && grossInput;

    // Предварительный расчёт — на сервере тем же движком, что и отчёт (с учётом уже проведённых выплат за месяц)
    useEffect(() => {
        if (!isOpen || !org || !official || items.length === 0 || editing) { setPreview(null); return; }
        const timer = setTimeout(() => {
            api.payrollPreview({ organizationId: org.id, date, kind: 'SALARY', salaryPart: regular ? part : null,
                                 salaryPeriod: period ? `${period}-01` : null, accrualType: accrual, grossInput: useGross, items })
                .then(setPreview).catch(() => setPreview(null));
        }, 300);
        return () => clearTimeout(timer);
    }, [isOpen, org, official, regular, items, date, part, period, editing, accrual, useGross]);

    const vacationEnd = periodFrom && Number(days) > 0 ? addDays(periodFrom, Number(days) - 1) : '';
    const payBy = accrual === 'VACATION' && periodFrom ? addDays(periodFrom, -3) : '';
    const latePay = !!payBy && date > payBy;

    const estimate = async () => {
        if (!org || !periodFrom || !(Number(days) > 0)) { showNotification('Укажите начало отпуска и число дней', 'error'); return; }
        const ids = Object.keys(amounts);
        if (ids.length === 0) { showNotification('Отметьте сотрудника', 'error'); return; }
        try {
            const result: Record<string, VacationEstimate> = {};
            for (const id of ids) result[id] = await api.vacationEstimate(org.id, id, periodFrom, Number(days));
            setEstimates(result);
            setGrossInput(true);
            setAmounts(Object.fromEntries(ids.map(id => [id, String(result[id].gross || '')])));
        } catch (e: any) { showNotification(e?.message || 'Не удалось рассчитать', 'error'); }
    };

    const scaleByDays = () => {
        const w = Number(worked), n = Number(norm);
        if (!(n > 0) || !(w >= 0) || w > n) { showNotification('Отработано — от 0 до нормы дней', 'error'); return; }
        setAmounts(prev => Object.fromEntries(Object.entries(prev).map(([k, v]) => [k, v ? String(Math.round(Number(v) * w / n * 100) / 100) : v])));
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (items.length === 0) { showNotification('Укажите сумму хотя бы одному сотруднику', 'error'); return; }
        // Неофициальной выплате вид начисления, аванс/расчёт и месяц не нужны — налоги по ней не считаются
        const title = !official ? (accrual === 'BONUS' ? 'Премия' : 'Зарплата') : regular ? (part === 'ADVANCE' ? 'Аванс' : 'Зарплата') : ACCRUAL_TITLES[accrual];
        const withPeriod = accrual === 'VACATION' || accrual === 'SICK';
        const common = {
            type: TransactionType.EXPENSE, date, status: TransactionStatus.PAID, kind: payType,
            category: title, description: !official ? `${title} (неофициально)` : regular ? `${title} за ${periodLabel(period)}`
                : withPeriod && periodFrom ? `${title}: ${formatDay(periodFrom)}${vacationEnd ? ` – ${formatDay(vacationEnd)}` : ''}${days ? `, ${days} дн.` : ''}`
                : `${title}, ${periodLabel(period)}`,
            hasVat: false, vatRate: 0, hasDocument: true, items: [],
            salaryPart: regular ? part : null, salaryPeriod: official && period ? `${period}-01` : null,
            accrualType: official ? accrual : null,
            payoutLabel: accrual === 'BONUS' ? 'BONUS' as const : 'SALARY' as const,
            periodFrom: official && withPeriod && periodFrom ? periodFrom : null,
            periodTo: official && withPeriod && periodFrom ? (accrual === 'VACATION' ? vacationEnd : addDays(periodFrom, Math.max(0, Number(days || 1) - 1))) || null : null,
            days: official && withPeriod && days ? Number(days) : null,
            paymentMethod: method, bankAccountId: method === 'bank' ? accountId : null,
            projectId: projectId || null,
        };
        const money = (i: { amount: number }) => useGross ? { amount: 0, grossAmount: i.amount } : { amount: i.amount, grossAmount: null };
        rememberOrg();
        if (editing) {
            updateTransaction(editing.id, { ...common, ...money(items[0]), ...(reviewed ? { reviewNote: null } : {}) });
            onClose();
            return;
        }
        if (official && accrual !== 'SALARY') {
            // Разовые начисления — сразу через API: нужны id операций для приказа (Т-11 из премии)
            const created: Transaction[] = [];
            try {
                for (const i of items) {
                    const id = `tx_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
                    created.push(await api.addTransaction({ ...common, ...money(i), id, organizationId: orgId, counterpartyId: i.counterpartyId,
                                                            documents: [] } as unknown as Transaction));
                }
                if (accrual === 'BONUS' && withOrder) {
                    for (const t of created) await api.createHrDocument(t.counterpartyId!, { kind: 'ORDER_BONUS', organizationId: orgId, date, transactionId: t.id });
                    showNotification(`Премия проведена, приказ Т-11 — в карточке сотрудника (вкладка «Документы»)`, 'success');
                } else showNotification(`${ACCRUAL_TITLES[accrual]}: проведено ${created.length}`, 'success');
            } catch (err: any) { showNotification(err?.message || 'Ошибка сохранения', 'error'); }
            await addTransactions([]);  // обновить ленту и сводку
            onPosted?.(created);
        } else {
            await addTransactions(items.map(i => ({ ...common, ...money(i), organizationId: orgId, counterpartyId: i.counterpartyId })));
        }
        onClose();
        if (canDraft && toBank) setDrafts({ orgId, date });
    };

    const sum = (key: 'gross' | 'ndfl' | 'net') => preview?.items.reduce((s, i) => s + i[key], 0) ?? 0;
    const monthContrib = preview?.items.reduce((s, i) => s + (i.monthContributions ?? 0), 0) ?? 0;
    const monthInjuries = preview?.items.reduce((s, i) => s + (i.monthInjuries ?? 0), 0) ?? 0;
    const noContrib = accrual === 'SICK';

    return (
        <>
        <Modal isOpen={isOpen} onClose={onClose} title={editing ? 'Выплата сотруднику' : 'Выплата сотрудникам'}>
            <form onSubmit={submit} className="space-y-3 text-sm">
                <PayKindTabs value={payType} withSelfEmployed={!editing && !!onSelfEmployed}
                    onChange={t => t === 'SELF_EMPLOYED' ? onSelfEmployed?.() : changeType(t)} />
                <OrgSelect orgs={orgs} value={orgId} onChange={v => { setOrgId(v); setAmounts({}); }} locked={!!fixedOrgId || !!editing} />
                {!official && (
                    <div className="space-y-1">
                        <label className="block text-sm font-medium text-slate-700">Вид выплаты</label>
                        <div className="inline-flex rounded border border-slate-300 overflow-hidden text-xs w-full">
                            {([['SALARY', 'Зарплата'], ['BONUS', 'Премия']] as const).map(([v, l]) => (
                                <button key={v} type="button" onClick={() => setAccrual(v)}
                                    className={`flex-1 px-2 py-2 ${accrual === v ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}>{l}</button>
                            ))}
                        </div>
                    </div>
                )}
                {official && (
                    <Select label="Вид начисления" value={accrual} onChange={e => changeAccrual(e.target.value as AccrualType)}
                        options={(Object.keys(ACCRUAL_LABELS) as AccrualType[]).map(k => ({ value: k, label: ACCRUAL_LABELS[k] }))} />
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Input label="Дата выплаты" type="date" value={date} onChange={e => changeDate(e.target.value)} required />
                    {official && !regular && (
                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-slate-700">{accrual === 'UNUSED_VACATION' ? 'Месяц увольнения' : 'Месяц начисления'}</label>
                            <input type="month" value={period} onChange={e => setPeriod(e.target.value)} required={!noContrib}
                                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
                        </div>
                    )}
                </div>
                {regular && <div className="flex flex-wrap items-center gap-2 text-xs">
                    <div className="inline-flex rounded border border-slate-300 overflow-hidden">
                        {([['ADVANCE', `Аванс (${org?.advanceDay ?? 25}-го)`], ['FINAL', `Расчёт (${org?.settlementDay ?? 10}-го)`]] as const).map(([v, l]) => (
                            <button key={v} type="button" onClick={() => { setPart(v); setPeriod(defaultSalaryPeriod(date, v)); }}
                                className={`px-2 py-1 ${part === v ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}>{l}</button>
                        ))}
                    </div>
                    <span className="text-slate-500">за</span>
                    <input type="month" value={period} onChange={e => setPeriod(e.target.value)} required
                        className="rounded border border-slate-300 px-2 py-1 text-xs" />
                </div>}
                {(accrual === 'VACATION' || accrual === 'SICK') && official && (
                    <div className="rounded border border-slate-200 bg-slate-50 p-2 space-y-2 text-xs">
                        <div className="grid grid-cols-2 gap-2">
                            <Input label={accrual === 'VACATION' ? 'Начало отпуска' : 'Начало больничного'} type="date" value={periodFrom} onChange={e => setPeriodFrom(e.target.value)} />
                            <Input label={accrual === 'VACATION' ? 'Дней отпуска' : 'Дней за счёт работодателя'} type="number" min={1} value={days} onChange={e => setDays(e.target.value)} placeholder={accrual === 'SICK' ? '3' : '14'} />
                        </div>
                        {accrual === 'VACATION' && <>
                            {vacationEnd && <p className="text-slate-600">Отпуск по {formatDay(vacationEnd)}. Отпускные — не позднее {formatDay(payBy)} (за 3 дня до начала).</p>}
                            <Button type="button" size="sm" variant="outline" onClick={estimate}><Calculator className="mr-1 h-3.5 w-3.5" /> Предварительный расчёт</Button>
                            {Object.entries(estimates).map(([id, est]) => (
                                <p key={id} className="text-slate-600">{staff.find(s => s.id === id)?.name}: выплаты за 12 мес. {formatMoney(est.base)} / ({est.workedMonths} мес. × 29,3)
                                    = {formatMoney(est.avgDaily)} в день × {est.days} дн. = <strong>{formatMoney(est.gross)}</strong> начислено</p>
                            ))}
                            {Object.keys(estimates).length > 0 && <p className="text-slate-500">Расчёт предварительный — сумму можно заменить расчётом бухгалтера.</p>}
                        </>}
                        {accrual === 'SICK' && <p className="text-slate-500">Сумму за первые 3 дня — из расчёта бухгалтера (средний заработок за 2 года, стаж). С 4-го дня пособие платит СФР напрямую сотруднику — здесь не вводится. Взносы с больничного не начисляются.</p>}
                    </div>
                )}
                {latePay && <p className="text-[11px] text-red-700 flex items-center gap-1"><AlertTriangle size={12} /> Отпускные нужно выплатить не позднее {formatDay(payBy)} — за 3 дня до начала отпуска.</p>}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Select label="Способ оплаты" value={method === 'cash' ? 'cash' : accountId}
                        onChange={e => { if (e.target.value === 'cash') setMethod('cash'); else { setMethod('bank'); setAccountId(e.target.value); } }}
                        options={[...(org?.accounts || []).map(a => ({ value: a.id, label: accountLabel(a) })), { value: 'cash', label: 'Наличные' }]} />
                    {official && (
                        <div className="space-y-1">
                            <label className="block text-sm font-medium text-slate-700">Сумма вводится</label>
                            <div className="inline-flex rounded border border-slate-300 overflow-hidden text-xs w-full">
                                {([[false, 'на руки'], [true, 'начислено']] as const).map(([v, l]) => (
                                    <button key={l} type="button" onClick={() => setGrossInput(v)}
                                        className={`flex-1 px-2 py-2 ${grossInput === v ? 'bg-blue-600 text-white' : 'bg-white text-slate-600 hover:bg-slate-100'}`}>{l}</button>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <div className="border rounded-lg border-slate-300 overflow-hidden">
                    <div className="bg-slate-100 px-2 py-1.5 text-[10px] font-semibold text-slate-600 flex justify-between">
                        <span>Сотрудники и сумма {useGross ? '«начислено»' : 'на руки'}</span>
                        {!editing && people.length > 1 && (
                            <button type="button" className="text-blue-600" onClick={() => setAmounts(Object.fromEntries(people.map(p => [p.id, amounts[p.id] || ''])))}>
                                Выбрать всех
                            </button>
                        )}
                    </div>
                    {people.length === 0 && <div className="p-3 text-xs text-slate-400">Добавьте сотрудников в справочнике</div>}
                    {people.map(p => {
                        const checked = p.id in amounts;
                        const pv = preview?.items.find(i => i.counterpartyId === p.id);
                        return (
                            <div key={p.id} className="flex flex-wrap items-center gap-2 px-2 py-1.5 border-t border-slate-100">
                                {!editing && <input type="checkbox" checked={checked} onChange={() => setAmounts(prev => {
                                    const next = { ...prev }; if (checked) delete next[p.id]; else next[p.id] = ''; return next; })} />}
                                <span className="flex-1 min-w-0 truncate">{p.name}</span>
                                <input type="number" min={0} step="0.01" placeholder="0" disabled={!editing && !checked}
                                    value={amounts[p.id] ?? ''} onChange={e => setAmounts(prev => ({ ...prev, [p.id]: e.target.value }))}
                                    className="w-28 rounded border border-slate-300 px-2 py-1 text-right disabled:bg-slate-50" />
                                {payType === 'UNOFFICIAL_SALARY' && !editing && <CrmDebtHint balances={crmBalances} counterpartyId={p.id} compact />}
                                {pv && <span className="basis-full text-right text-[10px] text-slate-500">
                                    {useGross ? `на руки ${formatMoney(pv.net)}` : `начислено ${formatMoney(pv.gross)}`} · НДФЛ {formatMoney(pv.ndfl)}</span>}
                            </div>
                        );
                    })}
                    <div className="flex justify-between px-2 py-1.5 bg-slate-50 border-t text-xs"><span>Итого {useGross ? 'начислено' : 'на руки'}</span><strong>{formatMoney(total)}</strong></div>
                </div>
                {regular && !editing && (
                    <div className="flex flex-wrap items-end gap-2 text-xs">
                        <span className="text-slate-500 pb-1.5">Неполный месяц (отпуск, больничный): отработано</span>
                        <input type="number" min={0} value={worked} onChange={e => setWorked(e.target.value)} className="w-14 rounded border border-slate-300 px-1 py-1" placeholder="дн." />
                        <span className="text-slate-500 pb-1.5">из</span>
                        <input type="number" min={1} value={norm} onChange={e => setNorm(e.target.value)} className="w-14 rounded border border-slate-300 px-1 py-1" placeholder="норма" />
                        <Button type="button" size="sm" variant="outline" onClick={scaleByDays}>Пересчитать суммы</Button>
                    </div>
                )}
                {notEmployedHere.length > 0 && (
                    <p className="text-[11px] text-slate-500">Не оформлены в этой организации (официально платить нельзя, только неофициально): {notEmployedHere.map(s => s.name).join(', ')}</p>
                )}
                <ProjectSelect projects={projects} value={projectId} onChange={setProjectId} />
                {official && accrual === 'BONUS' && !editing && (
                    <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={withOrder} onChange={e => setWithOrder(e.target.checked)} />
                        Сформировать приказ о премировании (Т-11)</label>
                )}

                {payType === 'UNOFFICIAL_SALARY' && (
                    <div className="p-2 rounded border text-xs bg-slate-50 border-slate-200 text-slate-600">
                        Неофициальная выплата: налоги не начисляются, в налоговую базу не входит — только управленческий ФОТ.
                    </div>
                )}
                {preview && (
                    <div className="p-3 rounded-lg border text-xs bg-orange-50 border-orange-100 text-orange-900 space-y-1">
                        <div className="flex items-center gap-2 font-bold"><Calculator size={14}/> Налоги по этой выплате</div>
                        <div className="flex justify-between gap-2"><span>Начислено по выплате</span><span className="whitespace-nowrap">{formatMoney(sum('gross'))}</span></div>
                        {useGross && <div className="flex justify-between gap-2"><span>На руки</span><span className="whitespace-nowrap">{formatMoney(sum('net'))}</span></div>}
                        <div className="flex justify-between gap-2"><span>НДФЛ удержать (до {formatDay(preview.ndflDue)})</span><strong className="whitespace-nowrap">{formatMoney(sum('ndfl'))}</strong></div>
                        {noContrib ? <div className="text-orange-800/80">Больничный: страховые взносы и травматизм не начисляются.</div>
                         : regular && part !== 'FINAL' ? <div className="text-orange-800/80">Взносы и травматизм начислятся при расчёте за {periodLabel(period)}.</div>
                         : <>
                            <div className="flex justify-between gap-2"><span>Взносы за {periodLabel(period)} — всего за месяц (до {formatDay(preview.contributionsDue)})</span><span className="whitespace-nowrap">{formatMoney(monthContrib)}</span></div>
                            <div className="flex justify-between gap-2"><span>Травматизм за {periodLabel(period)} (до {formatDay(preview.injuriesDue)}, СФР)</span><span className="whitespace-nowrap">{formatMoney(monthInjuries)}</span></div>
                            {!regular && <div className="text-orange-800/80">Отдельный платёж не создаётся: сумма войдёт во взносы за месяц начисления.</div>}
                        </>}
                    </div>
                )}

                {!editing && tbank.includes(orgId) && (
                    <div className="p-2 rounded-lg border border-slate-200 text-xs space-y-1">
                        <label className={`flex items-center gap-2 ${canDraft ? 'cursor-pointer' : 'text-slate-400'}`}>
                            <input type="checkbox" checked={canDraft && toBank} disabled={!canDraft} onChange={e => setToBank(e.target.checked)} />
                            <span className="font-medium">Черновики в Т-Банке после проведения</span>
                        </label>
                        <p className="text-[11px] text-slate-500 ml-5">
                            {canDraft ? 'Откроется окно: зарплата (реестр или платёжки по реквизитам из карточек), ЕНП на НДФЛ и на взносы, травматизм — проверите суммы и создадите черновики. Подписываете в Т-Бизнесе.'
                                : !official ? 'Только для официальной зарплаты.' : 'Только при оплате с расчётного счёта в Т-Банке.'}
                            {' '}<button type="button" className="text-blue-600 hover:underline" onClick={() => { onClose(); setDrafts({ orgId, date }); }}>
                                Черновики за {formatDay(date)} по уже проведённым выплатам и налогам</button>
                        </p>
                    </div>
                )}
                <ReviewCheckbox note={editing?.reviewNote} checked={reviewed} onChange={setReviewed} />
                <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                    <Button type="button" variant="secondary" size="sm" onClick={onClose}>Отмена</Button>
                    <Button type="submit" variant="danger" size="sm">{editing ? 'Сохранить' : 'Провести'}</Button>
                </div>
            </form>
        </Modal>
        {drafts && <PaymentDrafts orgIds={tbank} initialOrgId={drafts.orgId} initialDate={drafts.date} onClose={() => setDrafts(null)} />}
        </>
    );
};
